import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import usePageMeta from '../hooks/usePageMeta';
import useSectionViewTracking from '../hooks/useSectionViewTracking';
import { trackEvent } from '../analytics';
import './LongTermTimelapseGuidePage.css';

// Centralize every outbound product URL here so we can (a) audit them in tests
// and (b) later append affiliate tags in ONE place. Every URL was verified
// against the manufacturer's live site at author time; if a specific model
// page can't be verified, link the manufacturer's category page instead —
// never a guessed URL.
export const PRODUCT_LINKS = {
  brinnoTlc300: 'https://brinno.com/pages/brinno-tlc300-long-term-time-lapse-camera',
  brinnoBcc2000: 'https://brinno.com/pages/brinno-bcc2000-construction-time-lapse-camera',
  brinnoCompare: 'https://brinno.com/pages/brinno-time-lapse-camera-compare-guide',
  afidusAtl800: 'https://www.afidus.com/ATL-800.html',
  afidusHome: 'http://www.afidus.com/',
  goproHero13: 'https://gopro.com/en/us/shop/cameras/buy/hero13black/CHDHX-131-master.html',
  goproEnduro: 'https://gopro.com/en/us/shop/mounts-accessories/enduro-battery/AEBAT-001.html',
  camdoDryX: 'https://cam-do.com/products/dryx-weatherpoof-enclosure',
  camdoSolarX: 'https://cam-do.com/products/solarx-gopro-solar-system',
  camdoEnclosures: 'https://cam-do.com/collections/enclosures',
  reolinkTimelapse: 'https://store.reolink.com/us/time-lapse-camera/',
  reolinkSupport: 'https://support.reolink.com/articles/900002907866-Which-Reolink-Products-Support-Time-Lapse/',
};

// Affiliate disclosure text — render only when non-null. We are affiliate-ready
// but not currently monetizing these links, so disclosure stays null.
export const AFFILIATE_DISCLOSURE = null;

const SECTION_IDS = [
  'guide_intro',
  'guide_cameras',
  'guide_power',
  'guide_settings',
  'guide_shooting_window',
  'guide_editing',
  'guide_checklist',
  'guide_faq',
];

const TOC = [
  { id: 'guide_intro', label: 'Why long-term timelapses fail (and how to prevent it)' },
  { id: 'guide_cameras', label: 'Step 1: Choose your camera' },
  { id: 'guide_power', label: 'Step 2: Power and mounting' },
  { id: 'guide_settings', label: 'Step 3: Dial in your settings' },
  { id: 'guide_shooting_window', label: 'Step 4: Set your shooting window' },
  { id: 'guide_editing', label: 'Step 5: Turn 10,000 frames into a video people watch' },
  { id: 'guide_checklist', label: 'Pre-flight checklist' },
  { id: 'guide_faq', label: 'FAQ' },
];

const FAQ = [
  {
    q: 'How long can a timelapse camera run unattended?',
    a: 'Purpose-built long-term timelapse cameras like the Brinno TLC300 run 3+ months on 4 AA batteries at typical intervals. With a solar or wired-power kit, deployments of 6-12 months are routine. Action cameras and phones need constant power (USB-C from a wall or solar controller) to run more than a few days.',
  },
  {
    q: 'How much storage do I need for a 6-month build?',
    a: 'A safe rule: capture at your target resolution, 8-16 MP per still, then multiply by frames. A 6-month construction shoot at a 10-minute interval, 10 hours of active capture per weekday, produces roughly 78,000 stills — about 800 GB at 10 MP. Use the calculator to get an exact number for your project.',
  },
  {
    q: 'Can I use my phone for a long-term timelapse?',
    a: 'Yes, for indoor projects with constant wall power and a locked-down mount. A spare phone plus a timelapse app is the cheapest way to start. For outdoor multi-month shoots, a purpose-built timelapse camera is dramatically more reliable — no OS updates, no thermal throttling, weeks of battery life.',
  },
  {
    q: 'What happens on weekends and overnight?',
    a: 'Turn the camera off. Most projects have obvious idle windows — nights, weekends, holidays — where nothing happens. Shooting through them triples your storage, buries the interesting frames, and makes editing harder. Set a shooting window that matches the actual work schedule.',
  },
  {
    q: 'How do I remove night footage from timelapse automatically?',
    a: 'Two layers. First, do not shoot it: set the camera\'s schedule so the shooting window ends when the light does — that is the cheapest fix and it saves the storage too. When you cannot (a camera without a scheduler, or a site that runs late), let the edit handle it: automatic stillness detection reads the whole clip and trims the overnight stillness, so the dark, unchanging frames never reach the cut.',
  },
  {
    q: 'How do I remove flicker from a long-term timelapse?',
    a: 'Prevent it at capture: lock exposure, ISO, white balance, and focus. Auto modes cause every-frame drift that looks like flicker on playback. If flicker still shows up (common with changing daylight), Tweedia Studio applies temporal deflicker on render — you upload the raw stills or clip and export a smooth video.',
  },
  {
    q: 'What interval should I use for a construction timelapse?',
    a: 'Most construction projects look best at a 5-15 minute interval during active work hours. Faster (1-2 min) captures crane and framing action beautifully but produces a giant frame count; slower (30 min) misses the smaller day-to-day changes people love watching. Start at 10 minutes and use the calculator to fine-tune to your target video length.',
  },
];

const CAMERA_TABLE = [
  {
    option: 'Spare phone + timelapse app',
    cost: '$0 (if you have a spare)',
    power: 'USB wall power',
    weather: 'No — indoor only',
    bestFor: 'Indoor workshop, garage, hobby builds',
  },
  {
    option: 'Brinno TLC300 / BCC2000 bundle',
    cost: '$180-$500',
    power: '4-16 AA (weeks to months)',
    weather: 'Yes (BCC housing)',
    bestFor: 'The sweet spot — construction, renovation',
  },
  {
    option: 'Afidus ATL-800',
    cost: '~$500',
    power: 'Battery / DC in',
    weather: 'Yes',
    bestFor: '4K construction and outdoor projects',
  },
  {
    option: 'GoPro HERO + Enduro battery',
    cost: '~$400 + accessories',
    power: 'Enduro batt (short) or USB-C (long)',
    weather: 'With enclosure',
    bestFor: 'Higher fidelity, shorter shoots',
  },
  {
    option: 'GoPro + CamDo DryX/SolarX enclosure',
    cost: '$800-$1,500',
    power: 'Solar or external battery',
    weather: 'Yes — off-grid rated',
    bestFor: 'Remote, off-grid, multi-month',
  },
  {
    option: 'Reolink PoE camera (timelapse mode)',
    cost: '$150-$300',
    power: 'PoE (always on)',
    weather: 'Yes (IP66/IP67)',
    bestFor: 'Sites with an Ethernet run + remote check-in',
  },
  {
    option: 'Mirrorless / DSLR + intervalometer',
    cost: '$700+',
    power: 'AC required',
    weather: 'Needs housing',
    bestFor: 'Maximum image quality, indoor / covered',
  },
];

const INTERVAL_TABLE = [
  { type: 'Multi-month construction', interval: '5-15 minutes' },
  { type: 'Interior renovation', interval: '2-5 minutes' },
  { type: 'Garden / plant growth', interval: '15-30 minutes' },
  { type: 'Workshop / build project', interval: '15-60 seconds' },
  { type: 'Event setup / teardown', interval: '~1 minute' },
];

const CHECKLIST = [
  'Power source secured (wall, PoE, or solar) — no lone batteries for multi-month runs',
  'Camera physically locked down; mount cannot rotate or shift under wind',
  'Frame wider than the project needs today (leave room for growth)',
  'Exposure, ISO, white balance, and focus manually locked',
  'Interval calculated for your project length and target video runtime',
  'Storage sized with 30% headroom over the calculated frame count',
  'Shooting window set to active hours only (nights/weekends off)',
  'Test capture reviewed for framing, exposure, and timestamp overlay off',
  'Waterproofing / housing seals inspected if outdoors',
  'Remote check-in method decided (SD swap schedule, Wi-Fi, PoE, or 4G)',
];

function buildJsonLd() {
  const url = 'https://tweediastudio.com/guides/long-term-timelapse';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${url}#article`,
        headline: 'How to Time-Lapse a Long-Term Project: Cameras, Settings & Editing',
        description:
          'A practical, tested walkthrough for capturing and editing long-term timelapses — construction, renovation, garden, or workshop.',
        mainEntityOfPage: url,
        author: { '@type': 'Organization', name: 'Tweedia Studio', url: 'https://tweediastudio.com' },
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
        image: 'https://tweediastudio.com/tweedia_tour_poster.jpg',
      },
      {
        '@type': 'HowTo',
        '@id': `${url}#howto`,
        name: 'How to Time-Lapse a Long-Term Project',
        description:
          'Five steps to capture months of work as a video people actually watch: pick your camera, secure power and mounting, dial in settings, set a shooting window, and edit intelligently.',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Choose your camera',
            url: `${url}#guide_cameras`,
            text: 'Pick a camera matched to your project length and environment — phone for indoor, dedicated timelapse cam for outdoor multi-month, action cam for shorter, PoE camera for wired sites.',
          },
          {
            '@type': 'HowToStep',
            name: 'Power and mounting',
            url: `${url}#guide_power`,
            text: 'Prefer wall power or PoE over batteries; use a solar kit off-grid; lock the mount so it cannot be nudged; frame wider than you think.',
          },
          {
            '@type': 'HowToStep',
            name: 'Dial in your settings',
            url: `${url}#guide_settings`,
            text: 'Pick an interval that matches your project type; lock exposure, focus, and white balance to prevent flicker.',
          },
          {
            '@type': 'HowToStep',
            name: 'Set your shooting window',
            url: `${url}#guide_shooting_window`,
            text: 'Capture only during active hours; skip nights and weekends unless the work continues.',
          },
          {
            '@type': 'HowToStep',
            name: 'Edit intelligently',
            url: `${url}#guide_editing`,
            text: 'Even a perfect capture leaves hours of stillness. Use automatic stillness detection (e.g. Tweedia Studio) to trim the stillness and deflicker on render.',
          },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

function LongTermTimelapseGuidePage({ user, signIn }) {
  const signedIn = !!user;

  usePageMeta({
    title: 'How to Time-Lapse a Long-Term Project: Cameras, Settings & Editing | Tweedia Studio',
    description:
      'The definitive practical guide to shooting and editing a long-term timelapse — construction, renovation, garden, or workshop. Cameras, intervals, power, storage, and the edit.',
    canonicalPath: '/guides/long-term-timelapse',
    jsonLd: buildJsonLd(),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const setSectionRef = useSectionViewTracking(SECTION_IDS);

  const handleStudioCta = () => {
    trackEvent('cta_click', { cta_id: 'guide_try_studio', signed_in: signedIn });
    if (!signedIn) signIn();
  };

  const handleCalcNavClick = () => {
    trackEvent('nav_click', { target: 'calculator_from_guide' });
  };

  const outbound = (href, children, extraProps = {}) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...extraProps}>
      {children}
    </a>
  );

  const studioCta = signedIn ? (
    <Link
      to="/studio"
      className="guide-primary-cta"
      data-testid="guide-studio-cta"
      onClick={handleStudioCta}
    >
      Try Tweedia Studio
    </Link>
  ) : (
    <button
      type="button"
      className="guide-primary-cta"
      data-testid="guide-studio-cta"
      onClick={handleStudioCta}
    >
      Try Tweedia Studio
    </button>
  );

  return (
    <div className="guide-page">
      <Header user={user} signIn={signIn} />
      <main className="guide-content" data-testid="guide-content">
        <p className="guide-eyebrow">Guide</p>
        <h1>How to Time-Lapse a Long-Term Project</h1>
        <p className="guide-meta">Cameras, settings, power, and the edit. 12 min read.</p>
        <p className="guide-lede">
          Months of work compressed into a minutes-long video people actually watch — that is the
          promise of a long-term timelapse. Most attempts fail for a small handful of preventable
          reasons: a dead battery, a bumped camera, the wrong interval, or an unwatchable raw cut
          that never gets edited. This guide walks through every one of those failure modes and
          how to design around them, then shows the modern edit path that finally makes the whole
          thing viable for solo builders and small crews.
        </p>

        <nav className="guide-toc" aria-label="Table of contents">
          <p className="guide-toc-title">Contents</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section
          id="guide_intro"
          data-section-id="guide_intro"
          ref={setSectionRef('guide_intro')}
        >
          <h2>Why long-term timelapses fail (and how to prevent it)</h2>
          <p>
            The camera dies. The mount slips. The interval was set for a workshop build, not a
            six-month construction job, and you end up with 300,000 frames you cannot possibly
            review. Or — most commonly — the capture works fine, but the raw footage is so long
            and so full of stillness that the edit never happens. Every step in this guide exists
            to eliminate one of those failure modes.
          </p>
          <p>
            The good news: none of the fixes are exotic. Pick the right camera for the length of
            your project, feed it constant power, lock the frame and settings, capture only when
            work is happening, and use automatic stillness detection to compress the result.
            That is the whole game.
          </p>
        </section>

        <section
          id="guide_cameras"
          data-section-id="guide_cameras"
          ref={setSectionRef('guide_cameras')}
        >
          <h2>Step 1: Choose your camera</h2>
          <p>
            There is no single "best" long-term timelapse camera — there is the right camera for
            the length, location, and quality bar of <em>your</em> project. Match the tool to
            the shoot and everything downstream gets easier. Here is the honest at-a-glance
            comparison, followed by when each option makes sense.
          </p>

          <div className="guide-table-wrap">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Option</th>
                  <th>Typical cost</th>
                  <th>Battery / power</th>
                  <th>Weatherproof</th>
                  <th>Best for</th>
                </tr>
              </thead>
              <tbody>
                {CAMERA_TABLE.map((row) => (
                  <tr key={row.option}>
                    <td>{row.option}</td>
                    <td>{row.cost}</td>
                    <td>{row.power}</td>
                    <td>{row.weather}</td>
                    <td>{row.bestFor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="guide-option">
            <h3>Spare phone + timelapse app</h3>
            <p>
              If you have a phone gathering dust in a drawer, this is the cheapest way to start.
              Plug it into a wall outlet, clamp it in place with a phone tripod mount, and run a
              timelapse app. It works surprisingly well for indoor workshop projects, garage
              builds, and short-run renovations — anywhere you have constant power and a stable,
              temperature-controlled environment.
            </p>
            <p>
              What kills a phone timelapse outdoors: heat, moisture, OS updates that restart the
              app mid-shoot, and battery degradation over months of trickle charging. Fine for
              days-to-weeks; unreliable past that.
            </p>
          </div>

          <div className="guide-option">
            <h3>Dedicated long-term timelapse cameras (the sweet spot)</h3>
            <p>
              This is what most working professionals use: cameras designed from the ground up to
              sit outdoors for months and shoot one frame at a time. Battery life is measured in
              weeks or months on a handful of AAs, weatherproof housings are engineered in, and
              the interface is a single-purpose knob-and-menu rig — no OS to fight with.
            </p>
            <p>
              The{' '}
              {outbound(
                PRODUCT_LINKS.brinnoTlc300,
                'Brinno TLC300',
                { 'data-testid': 'link-brinno-tlc300' }
              )}{' '}
              runs up to ~100 days on 4 AA batteries at 1080p — a fantastic entry point. Step up
              to the{' '}
              {outbound(
                PRODUCT_LINKS.brinnoBcc2000,
                'Brinno BCC2000 Construction Bundle',
                { 'data-testid': 'link-brinno-bcc2000' }
              )}{' '}
              for the ATH2000 weatherproof housing (holds up to 16 AAs for year-long deployments)
              and the industrial clamp mount. If you need 4K, the{' '}
              {outbound(
                PRODUCT_LINKS.afidusAtl800,
                'Afidus ATL-800',
                { 'data-testid': 'link-afidus-atl800' }
              )}{' '}
              is the direct competitor.
            </p>
            <p className="guide-option-links">
              Compare all Brinno models:{' '}
              {outbound(PRODUCT_LINKS.brinnoCompare, 'Brinno compare guide')} · Manufacturer:{' '}
              {outbound(PRODUCT_LINKS.afidusHome, 'Afidus')}
            </p>
          </div>

          <div className="guide-option">
            <h3>Action cameras (GoPro)</h3>
            <p>
              A{' '}
              {outbound(
                PRODUCT_LINKS.goproHero13,
                'GoPro HERO13 Black',
                { 'data-testid': 'link-gopro-hero' }
              )}{' '}
              gives you dramatically higher image quality than a Brinno and native 4K/5.3K.
              Paired with an{' '}
              {outbound(PRODUCT_LINKS.goproEnduro, 'Enduro battery')} it will happily shoot
              short-run timelapses over a weekend or a week. For anything longer, you need
              constant power — a wall outlet, a big USB power bank, or an outdoor enclosure.
            </p>
            <p>
              For weeks-to-months outdoor deployments,{' '}
              {outbound(
                PRODUCT_LINKS.camdoDryX,
                'CamDo\'s DryX weatherproof enclosure',
                { 'data-testid': 'link-camdo-dryx' }
              )}{' '}
              (and its{' '}
              {outbound(
                PRODUCT_LINKS.camdoSolarX,
                'SolarX solar-powered variant',
                { 'data-testid': 'link-camdo-solarx' }
              )}
              ) turn a GoPro into a serious long-term rig. See all their{' '}
              {outbound(PRODUCT_LINKS.camdoEnclosures, 'enclosure options')}.
            </p>
          </div>

          <div className="guide-option">
            <h3>Security-camera route (Reolink)</h3>
            <p>
              An underrated option for construction sites: a PoE security camera with a built-in
              timelapse mode. You get constant power over the Ethernet cable, remote check-in
              from your phone, and IP66/IP67 weatherproofing already engineered in. Set it up
              once, watch the site remotely, download the timelapse when the project ends.
            </p>
            <p>
              Reolink markets specifically to construction with cameras like the RLC-811A and
              RLC-823A — see the{' '}
              {outbound(
                PRODUCT_LINKS.reolinkTimelapse,
                'Reolink time-lapse camera store',
                { 'data-testid': 'link-reolink' }
              )}{' '}
              for current models, and their{' '}
              {outbound(
                PRODUCT_LINKS.reolinkSupport,
                'compatibility list'
              )}{' '}
              for which models support the feature.
            </p>
          </div>

          <div className="guide-option">
            <h3>Mirrorless / DSLR + intervalometer</h3>
            <p>
              Maximum image quality — 20+ megapixels per frame, real lenses, and RAW files that
              hold up in the edit. Trade-offs: AC power is basically required (mirrorless
              batteries drain fast even in interval shooting), the shutter has a finite life, and
              you need weatherproof housing to leave one outside. Use this route when the shoot
              is indoor, covered, and image quality matters more than convenience.
            </p>
          </div>
        </section>

        <section
          id="guide_power"
          data-section-id="guide_power"
          ref={setSectionRef('guide_power')}
        >
          <h2>Step 2: Power and mounting</h2>
          <p>
            The number one and number two reasons long-term timelapses fail are, in order:{' '}
            <strong>the battery died</strong> and <strong>the camera got nudged</strong>. Fix
            both up front and the rest is easy.
          </p>
          <h3>Power</h3>
          <p>
            <strong>Prefer wall power over batteries whenever you can reach an outlet.</strong>{' '}
            A dedicated timelapse camera on 16 AAs will genuinely run for months, but a wall
            adapter runs forever. On construction sites, PoE (Power over Ethernet) is the
            professional-grade answer — one cable delivers power and pulls footage back to your
            desk. For off-grid deployments (remote land, unwired new construction, backyard
            garden projects), a small solar panel + charge controller kit is the standard rig;
            CamDo's SolarX enclosure is one turnkey option for GoPro, and Brinno sells the
            ASP1000-P solar kit for their timelapse cams.
          </p>
          <h3>Mounting</h3>
          <p>
            <strong>Lock the camera down like you never plan to touch it again</strong>, because
            you should not. Every time you check on the camera you risk shifting the frame — and
            in a timelapse a 3-degree rotation is instantly visible on playback. Use an
            industrial clamp mount, a scaffold arm, or a screw-fixed bracket. Tighten
            aggressively, mark the position on the mount with a paint pen so you can spot
            movement, and cover screws with thread-lock if the mount will experience vibration.
          </p>
          <h3>Frame wider than you think</h3>
          <p>
            The single most common framing regret is cropping too tight on Day 1 and watching
            the building grow out of frame by Month 3. Include everything the project will
            eventually occupy plus a comfortable margin. Wide is easy to crop in post; too tight
            is unfixable.
          </p>
          <h3>Weatherproofing</h3>
          <p>
            If the camera is outdoors: either use a purpose-built housing (Brinno ATH2000,
            CamDo DryX) or bring an IP-rated camera (Reolink). Duct tape and Ziploc bags are not
            waterproofing — they trap condensation and fog the lens by Week 2.
          </p>
          <h3>The "never touch it" rule</h3>
          <p>
            Every physical check on the camera introduces the risk of ending the shoot. Design
            around it: PoE with remote check-in, or a camera that reports battery over Wi-Fi, or
            a large enough SD card that you don't need to swap it. Touch the camera only to
            end the shoot.
          </p>
        </section>

        <section
          id="guide_settings"
          data-section-id="guide_settings"
          ref={setSectionRef('guide_settings')}
        >
          <h2>Step 3: Dial in your settings</h2>
          <p>
            Interval is everything. It controls how long the shoot can run, how much storage
            you need, how many frames the final video will contain, and how much of the raw
            footage feels alive vs sluggish. Get it right up front — you cannot re-shoot a
            missed month.
          </p>

          <div className="guide-table-wrap">
            <table className="guide-table guide-interval-table">
              <thead>
                <tr>
                  <th>Project type</th>
                  <th>Recommended interval</th>
                </tr>
              </thead>
              <tbody>
                {INTERVAL_TABLE.map((row) => (
                  <tr key={row.type}>
                    <td>{row.type}</td>
                    <td>{row.interval}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="guide-callout">
            <h3>Get your exact numbers</h3>
            <p>
              Enter your schedule and your target video length and get the exact interval,
              frame count, and storage size — no rules of thumb, real math.
            </p>
            <Link
              to="/tools/timelapse-calculator"
              className="guide-callout-cta"
              data-testid="guide-calculator-callout"
              onClick={handleCalcNavClick}
            >
              Open the Long-Term Timelapse Calculator
            </Link>
          </div>

          <h3>Lock everything that can auto-adjust</h3>
          <p>
            <strong>Manual exposure. Manual focus. Manual white balance. Manual ISO.</strong>{' '}
            Auto-anything is the number-one cause of flicker. When a camera decides frame 401
            needs a hair more exposure than frame 400, you see the jump on playback as a
            jarring brightness pulse. Set exposure for the typical midday scene, lock it, and
            accept that shadows will crush a little and highlights will clip a little at the
            edges of the day. Deflickering in post can fix the last 10%; it cannot rescue a
            fully auto-mode capture.
          </p>
          <h3>Shoot stills, not video, when you can</h3>
          <p>
            A sequence of high-resolution stills gives you far more flexibility than a
            pre-encoded video: you can change the output framerate freely, crop and stabilize
            in post, and re-render at higher quality later. Purpose-built timelapse cameras
            handle this natively; GoPros expose a "Photo Time Lapse" mode; DSLRs do it with
            an intervalometer.
          </p>
          <h3>Highest resolution you can store</h3>
          <p>
            Storage is cheap, re-shooting is impossible. Shoot at the highest resolution the
            camera supports and downsample in post if you want smaller output. This gives you
            headroom to crop, reframe, or produce vertical Shorts from the same source.
          </p>
          <h3>Turn timestamps off</h3>
          <p>
            Burned-in timestamps date the footage instantly and are impossible to remove. If
            you want time context in the final video, add it in the edit as a proper title
            overlay.
          </p>
        </section>

        <section
          id="guide_shooting_window"
          data-section-id="guide_shooting_window"
          ref={setSectionRef('guide_shooting_window')}
        >
          <h2>Step 4: Set your shooting window</h2>
          <p>
            <strong>Capture only when work is happening.</strong> If your crew works
            7am–6pm Monday–Friday, that is your shooting window — everything else is hours of
            stillness that eat storage, bury the action, and make editing harder. Every
            reputable timelapse camera has a schedule setting; use it.
          </p>
          <p>
            Concrete math: a 10-minute-interval shoot over a 6-month construction project
            captures roughly 26,000 frames if you shoot only during 10-hour weekday work
            windows — an editable amount. The same shoot running 24/7 captures 78,000
            frames, three times the storage, and ~66% of those frames are pitch-black
            overnight stills of nothing.
          </p>
          <p>
            One caveat: even a perfect shooting window still yields hours of "nothing
            visibly happening" — a crew on lunch break, a sudden rain delay, a stalled
            inspection. That is fine. The point of a tight window is to get you 3x closer to
            a watchable raw cut, not to eliminate the stillness entirely. The edit finishes
            the job.
          </p>
        </section>

        <section
          id="guide_editing"
          data-section-id="guide_editing"
          ref={setSectionRef('guide_editing')}
        >
          <h2>Step 5: Turn 10,000 frames into a video people watch</h2>
          <p>
            Here is the honest truth about long-term timelapse projects, the one that kills
            most of them: <strong>at any capture interval, the majority of a long project is
            nothing visibly happening.</strong> Rain delays. Waiting on inspections. A
            weekend where you forgot to disable the schedule. Even after a perfect capture,
            you are staring down thousands of frames and the very real question of how to
            find the interesting ones.
          </p>
          <p>
            The traditional answer — hand-scrub the whole thing in a video editor, marking
            in/out points for every scene of action — is the single biggest reason
            long-term timelapse footage sits on drives, unedited, forever. It is genuinely
            days of tedious work. It is also the exact step Tweedia Studio automates.
          </p>
          <h3>What Tweedia Studio does</h3>
          <p>
            You upload your photo sequence or raw footage — from any of the cameras above —
            and Tweedia automatically:
          </p>
          <ul>
            <li>
              <strong>Detects stillness</strong> across the whole clip and finds the ranges
              where visible action is happening.
            </li>
            <li>
              <strong>Trims the stillness</strong> so you keep only the frames worth showing.
              If detection is too aggressive or too conservative for a particular segment, the
              studio lets you adjust the sensitivity manually per segment.
            </li>
            <li>
              <strong>Deflickers on render</strong>, applying temporal correction so the
              small exposure and white-balance drifts that survive even a locked-manual
              capture come out smooth.
            </li>
            <li>
              <strong>Exports</strong> at your source resolution — up to 4K, never
              downscaled by plan — ready to share.
            </li>
          </ul>
          <p>
            It’s free to start (no credit card). Subscribe when you’re ready for
            pooled cloud storage, monthly render credits, and watermark-free 4K
            exports — founding rates lock in while you stay subscribed.
          </p>
          <div className="guide-final-cta-row" style={{ marginTop: 20 }}>
            {studioCta}
          </div>
        </section>

        <section
          id="guide_checklist"
          data-section-id="guide_checklist"
          ref={setSectionRef('guide_checklist')}
        >
          <h2>Pre-flight checklist</h2>
          <p>
            Before you walk away from the camera for the next three months, run through
            this. Every item is here because at least one long-term timelapse has died on it.
          </p>
          <div className="guide-checklist" data-testid="guide-checklist">
            <h3>Before you leave the site</h3>
            <ul>
              {CHECKLIST.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section
          id="guide_faq"
          data-section-id="guide_faq"
          ref={setSectionRef('guide_faq')}
        >
          <h2>FAQ</h2>
          <div className="guide-faq" data-testid="guide-faq">
            {FAQ.map((item) => (
              <div className="guide-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="guide-final-cta">
          <h2>Ready to edit smart?</h2>
          <p>
            Bring your footage. Tweedia Studio trims the stillness, deflickers, and exports —
            free to start, with founding subscriptions for storage and watermark-free renders.
          </p>
          <div className="guide-final-cta-row">
            {studioCta}
            <Link
              to="/tools/timelapse-calculator"
              className="guide-secondary-cta"
              data-testid="guide-calculator-final"
              onClick={handleCalcNavClick}
            >
              Open the Calculator
            </Link>
          </div>
          <p className="guide-note">No credit card required to start editing.</p>
        </div>

        {AFFILIATE_DISCLOSURE && (
          <p
            className="guide-affiliate-disclosure"
            data-testid="guide-affiliate-disclosure"
          >
            {AFFILIATE_DISCLOSURE}
          </p>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default LongTermTimelapseGuidePage;
